import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Search, ArrowLeft, Filter, Grid, List, Calendar, Download, Share2, Trash2, MoreHorizontal, Loader2, Upload, Tag, Sparkles, Pencil, Euro, Users, Hash, AlertTriangle, Clock, ScanText, RefreshCw, FolderOpen, ScanLine, Plus, ExternalLink, CheckSquare, Square, Copy, ChevronLeft, ChevronRight, Eye, ZoomIn, ZoomOut, Star, FolderIcon, RotateCcw, Trash } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

import { DocumentUpload } from "@/components/documents/DocumentUpload";
import { ShareDialog } from "@/components/documents/ShareDialog";
import { TagManager } from "@/components/documents/TagManager";
import { DocumentEdit } from "@/components/documents/DocumentEdit";
import { PremiumAISearch, SearchResult } from "@/components/documents/PremiumAISearch";
import { InlineSearchResults } from "@/components/documents/InlineSearchResults";
import DocumentCollector from "@/components/documents/DocumentCollector";
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";
import { DocumentPreviewSheet } from "@/components/documents/DocumentPreviewSheet";
import { FAMILY_CONFIG, TYPE_CONFIG, getFamilyConfig, getTypeConfig, getFamilyFromCategory } from "@/constants/ui-mapping";
import { getDocumentTypeLabel, getDocumentTypeEmoji, isDocumentAnalyzing } from "@/components/documents/DocumentTypeBadge";
import { SEO } from "@/components/seo/SEO";
import { ActionsBadge } from "@/components/dashboard/ActionsBadge";
import { MoveToFolderDialog } from "@/components/documents/MoveToFolderDialog";
import { FolderBreadcrumb } from "@/components/documents/FolderBreadcrumb";
import { DraggableDocument } from "@/components/documents/DraggableDocument";
import { useFolders } from "@/hooks/useFolders";
import type { Tables } from "@/integrations/supabase/types";
import easyfinderLogo from "@/assets/easyfinder-logo-google.png";
import { Document as PDFDocument, Page } from "react-pdf";
import { pdfOptions } from "@/lib/pdfConfig";
import { useIsMobile } from "@/hooks/use-mobile";
import { SkeletonDocumentsList, SkeletonDocumentsGrid } from "@/components/ui/skeleton";
import { useBackgroundThumbnails } from "@/hooks/useBackgroundThumbnails";
import { isExpirableDocType } from "@/lib/documentExpiration";
import { AnalysisProgressBar } from "@/components/documents/AnalysisProgressBar";

type Document = Tables<"documents">;
type DocumentTag = Tables<"document_tags">;

interface DocumentWithTags extends Document {
  tags?: DocumentTag[];
  taxonomy_family?: string | null; // from document_taxonomy join
}

interface DuplicateGroup {
  key: string;
  documents: DocumentWithTags[];
}

// Density options for documents per page
const DENSITY_OPTIONS = [20, 50, 100] as const;

// Quick preview component for selection mode
const QuickPreviewContent = ({ doc }: { doc: Document }) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [containerWidth, setContainerWidth] = useState<number>(300);
  const [manualScale, setManualScale] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Measure container width for fit-to-width
  useEffect(() => {
    const measureWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.getBoundingClientRect().width;
        if (width > 50) {
          setContainerWidth(width - 20); // minus padding
        }
      }
    };
    
    // Measure after a small delay to ensure layout is complete
    measureWidth();
    const timer = setTimeout(measureWidth, 100);
    const timer2 = setTimeout(measureWidth, 500);
    
    window.addEventListener('resize', measureWidth);
    return () => {
      window.removeEventListener('resize', measureWidth);
      clearTimeout(timer);
      clearTimeout(timer2);
    };
  }, [loading, fileUrl]);

  useEffect(() => {
    const fetchFile = async () => {
      setLoading(true);
      setCurrentPage(1);
      setManualScale(null);
      const { data } = await supabase.storage
        .from("documents")
        .createSignedUrl(doc.file_path, 300);
      
      if (data?.signedUrl) {
        setFileUrl(data.signedUrl);
      }
      setLoading(false);
    };
    fetchFile();
  }, [doc.file_path]);

  const isPdf = doc.mime_type?.includes('pdf');
  const isImage = doc.mime_type?.includes('image');

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    // Re-measure after PDF loads
    if (containerRef.current) {
      const width = containerRef.current.getBoundingClientRect().width;
      if (width > 50) setContainerWidth(width - 20);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!fileUrl) {
    return (
      <div className="flex items-center justify-center h-48 text-muted-foreground">
        Aperçu non disponible
      </div>
    );
  }

  if (isImage) {
    return (
      <div className="flex items-center justify-center">
        <img 
          src={fileUrl} 
          alt={doc.title} 
          className="max-w-full max-h-[50vh] object-contain rounded-lg"
        />
      </div>
    );
  }

  if (isPdf) {
    const isAutoMode = manualScale === null;
    const effectiveWidth = isAutoMode ? Math.max(200, containerWidth) : undefined;
    
    return (
      <div ref={containerRef} className="flex flex-col items-center gap-2 w-full">
        {/* PDF Controls - compact */}
        <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1 flex-wrap justify-center">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
          >
            <ChevronLeft className="w-3 h-3" />
          </Button>
          <span className="text-[10px] min-w-[40px] text-center">
            {currentPage}/{numPages || '?'}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setCurrentPage(p => Math.min(numPages || p, p + 1))}
            disabled={!numPages || currentPage >= numPages}
          >
            <ChevronRight className="w-3 h-3" />
          </Button>
          <div className="h-3 w-px bg-border mx-0.5" />
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setManualScale(s => Math.max(0.5, (s || 1) - 0.25))}
          >
            <ZoomOut className="w-3 h-3" />
          </Button>
          <button
            className={`text-[9px] min-w-[32px] text-center px-1 py-0.5 rounded ${isAutoMode ? 'bg-primary/20 text-primary font-medium' : 'text-muted-foreground'}`}
            onClick={() => setManualScale(null)}
          >
            {isAutoMode ? 'Auto' : `${Math.round((manualScale || 1) * 100)}%`}
          </button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setManualScale(s => Math.min(2, (s || 1) + 0.25))}
          >
            <ZoomIn className="w-3 h-3" />
          </Button>
        </div>
        
        {/* PDF Viewer */}
        <div className="overflow-auto max-h-[45vh] border rounded bg-white w-full">
          <PDFDocument
            file={fileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              </div>
            }
            error={
              <div className="flex flex-col items-center justify-center h-40 gap-2">
                <FileText className="w-8 h-8 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Erreur de chargement</p>
                <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="h-6 text-[10px]">
                    <Download className="w-3 h-3 mr-1" />
                    Télécharger
                  </Button>
                </a>
              </div>
            }
          >
            <Page 
              pageNumber={currentPage} 
              width={effectiveWidth}
              scale={!isAutoMode ? manualScale : undefined}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </PDFDocument>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-40 gap-2">
      <FileText className="w-10 h-10 text-muted-foreground" />
      <p className="text-xs text-muted-foreground">Aperçu non disponible</p>
      <a href={fileUrl} target="_blank" rel="noopener noreferrer">
        <Button variant="outline" size="sm" className="h-6 text-[10px]">
          <Download className="w-3 h-3 mr-1" />
          Télécharger
        </Button>
      </a>
    </div>
  );
};

// Thumbnail component for document cards - uses pre-generated thumbnails for performance
// Includes Realtime listener for automatic thumbnail appearance when generated by background worker
const DocumentThumbnail = ({ doc, className = "" }: { doc: Document; className?: string }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Track thumbnail path in state to allow Realtime updates
  const [thumbnailPath, setThumbnailPath] = useState<string | null>(
    (doc as any).thumbnail_path
  );

  const isPdf = doc.mime_type?.includes('pdf');
  const isImage = doc.mime_type?.includes('image');
  const typeConfig = getTypeConfig(doc.doc_type);

  // Listen for Realtime updates when thumbnail is generated by background worker
  useEffect(() => {
    // Only subscribe if this is a PDF without a thumbnail
    if (!isPdf || thumbnailPath) return;

    const channel = supabase
      .channel(`doc-thumb-${doc.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'documents',
        filter: `id=eq.${doc.id}`
      }, (payload) => {
        const newPath = (payload.new as any)?.thumbnail_path;
        if (newPath && !thumbnailPath) {
          console.log('[DocumentThumbnail] Realtime update received:', doc.id);
          setThumbnailPath(newPath);
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [doc.id, isPdf, thumbnailPath]);

  useEffect(() => {
    const fetchThumbnail = async () => {
      setLoading(true);
      setError(false);
      
      // If we have a pre-generated thumbnail, use it (public bucket - no signed URL needed)
      if (thumbnailPath) {
        const { data: publicUrl } = supabase.storage
          .from("thumbnails")
          .getPublicUrl(thumbnailPath);
        
        if (publicUrl?.publicUrl) {
          setThumbnailUrl(publicUrl.publicUrl);
          setLoading(false);
          return;
        }
      }
      
      // Fallback: only fetch original for images (PDFs will show emoji)
      if (isImage) {
        const { data } = await supabase.storage
          .from("documents")
          .createSignedUrl(doc.file_path, 300);
        
        if (data?.signedUrl) {
          setThumbnailUrl(data.signedUrl);
        } else {
          setError(true);
        }
      } else {
        // For PDFs without thumbnail, don't try to render - show emoji
        setError(true);
      }
      setLoading(false);
    };
    fetchThumbnail();
  }, [doc.file_path, thumbnailPath, isImage]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-muted rounded ${className}`}>
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !thumbnailUrl) {
    return (
      <div className={`flex items-center justify-center bg-muted rounded text-2xl ${className}`}>
        {typeConfig.emoji}
      </div>
    );
  }

  // Pre-generated thumbnails and images are displayed the same way
  return (
    <motion.div 
      className={`overflow-hidden rounded bg-muted ${className}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <img 
        src={thumbnailUrl} 
        alt={doc.title}
        className="w-full h-full object-cover"
        loading="lazy"
        onError={() => setError(true)}
      />
    </motion.div>
  );
};

// Gallery view component for delete confirmation
const DeleteGalleryView = ({ 
  documents, 
  currentIndex, 
  onIndexChange,
  onDeselect,
  onBack,
  formatDate 
}: { 
  documents: Document[]; 
  currentIndex: number; 
  onIndexChange: (idx: number) => void;
  onDeselect: (docId: string) => void;
  onBack: () => void;
  formatDate: (date: string) => string;
}) => {
  const doc = documents[currentIndex];
  const typeLabel = getDocumentTypeLabel(doc?.doc_type, doc?.status, false);
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        onIndexChange(Math.max(0, currentIndex - 1));
      } else if (e.key === 'ArrowRight') {
        onIndexChange(Math.min(documents.length - 1, currentIndex + 1));
      } else if (e.key === 'Escape') {
        onBack();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, documents.length, onIndexChange, onBack]);

  if (!doc) return null;

  return (
    <div className="flex flex-col gap-2 my-2 max-h-[65vh] overflow-hidden">
      {/* Header row: Back + Title + Position */}
      <div className="flex items-center gap-2 pb-2 border-b shrink-0">
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground h-7 text-xs shrink-0"
          onClick={onBack}
        >
          <ArrowLeft className="w-3 h-3 mr-1" />
          Retour
        </Button>
        <div className="flex-1 text-center min-w-0">
          <p className="font-medium text-xs truncate">{doc.title}</p>
          <div className="flex items-center justify-center gap-1 mt-0.5">
            <span className="text-[9px] px-1 py-0.5 rounded bg-muted text-muted-foreground">
              {typeLabel}
            </span>
            <span className="text-[9px] text-muted-foreground">
              {formatDate(doc.created_at)}
            </span>
          </div>
        </div>
        <span className="text-xs text-muted-foreground shrink-0">
          {currentIndex + 1}/{documents.length}
        </span>
      </div>

      {/* Preview area with nav arrows */}
      <div className="flex items-stretch gap-1 flex-1 min-h-0 overflow-hidden">
        {/* Left arrow */}
        <Button
          variant="ghost"
          size="icon"
          className="h-auto w-8 shrink-0 self-center"
          onClick={() => onIndexChange(Math.max(0, currentIndex - 1))}
          disabled={currentIndex === 0}
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        
        {/* Preview container - contained box */}
        <div className="flex-1 min-w-0 overflow-auto rounded-lg border bg-background">
          <QuickPreviewContent doc={doc} />
        </div>
        
        {/* Right arrow */}
        <Button
          variant="ghost"
          size="icon"
          className="h-auto w-8 shrink-0 self-center"
          onClick={() => onIndexChange(Math.min(documents.length - 1, currentIndex + 1))}
          disabled={currentIndex === documents.length - 1}
        >
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Footer: Deselect + Thumbnails */}
      <div className="border-t pt-2 shrink-0 space-y-2">
        <div className="text-center">
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-7"
            onClick={() => onDeselect(doc.id)}
          >
            <Square className="w-3 h-3 mr-1" />
            Désélectionner
          </Button>
        </div>
        
        {/* Thumbnails strip */}
        <div className="flex items-center justify-center gap-1 overflow-x-auto pb-1">
          {documents.map((d, idx) => (
            <button
              key={d.id}
              onClick={() => onIndexChange(idx)}
              className={`w-8 h-8 rounded overflow-hidden border-2 transition-all shrink-0 ${
                idx === currentIndex 
                  ? 'border-primary ring-1 ring-primary/30' 
                  : 'border-muted/50 opacity-50 hover:opacity-100'
              }`}
            >
              <DocumentThumbnail doc={d} className="w-full h-full" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const Documents = () => {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const { openDocument } = useDocumentPreview();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  
  // URL-based view params
  const viewParam = searchParams.get("view"); // "recent" | "starred" | "trash"
  const folderParam = searchParams.get("folder"); // folder UUID
  const initialSearch = searchParams.get("search") || "";
  const initialFilter = searchParams.get("filter") || "";
  
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  
  // Load view preferences from localStorage with defaults
  const [viewMode, setViewMode] = useState<"grid" | "list" | "compact">(() => {
    const saved = localStorage.getItem('documents_viewMode');
    if (saved && ['grid', 'list', 'compact'].includes(saved)) {
      return saved as "grid" | "list" | "compact";
    }
    return "grid"; // Default to grid view
  });
  
  const [docsPerPage, setDocsPerPage] = useState<number>(() => {
    const saved = localStorage.getItem('documents_docsPerPage');
    if (saved && DENSITY_OPTIONS.includes(Number(saved) as typeof DENSITY_OPTIONS[number])) {
      return Number(saved);
    }
    return 50; // Default to 50 docs per page
  });
  const [typeFilter, setTypeFilter] = useState("all");
  const [familyFilter, setFamilyFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [minAmountFilter, setMinAmountFilter] = useState("");
  const [partyFilter, setPartyFilter] = useState("");
  const [expiringFilter, setExpiringFilter] = useState(initialFilter === "expiring");
  
  // Sort state
  type SortOption = "date_desc" | "date_asc" | "name_asc" | "name_desc" | "type";
  const [sortBy, setSortBy] = useState<SortOption>("date_desc");
  const [documents, setDocuments] = useState<DocumentWithTags[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [uploadOpen, setUploadOpen] = useState(false);
  const [shareDoc, setShareDoc] = useState<Document | null>(null);
  const [tagDoc, setTagDoc] = useState<Document | null>(null);
  const [editDoc, setEditDoc] = useState<Document | null>(null);
  // Inline search state (from PremiumAISearch)
  const [inlineSearchState, setInlineSearchState] = useState<{
    isSearching: boolean;
    searchQuery: string;
    searchResults: SearchResult[];
    isLoading: boolean;
    totalCount: number;
    hasMore: boolean;
    isLoadingMore: boolean;
    loadMore: () => void;
  }>({
    isSearching: false,
    searchQuery: "",
    searchResults: [],
    isLoading: false,
    totalCount: 0,
    hasMore: false,
    isLoadingMore: false,
    loadMore: () => {},
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Selection state
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);

  // Quick preview in selection mode
  const [quickPreviewDoc, setQuickPreviewDoc] = useState<Document | null>(null);

  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [galleryMode, setGalleryMode] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  // Duplicates dialog state
  const [duplicatesDialogOpen, setDuplicatesDialogOpen] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([]);
  const [selectedDuplicates, setSelectedDuplicates] = useState<Set<string>>(new Set());
  const [deletingDuplicates, setDeletingDuplicates] = useState(false);
  
  // Preview sheet for duplicates
  const [duplicatePreviewDoc, setDuplicatePreviewDoc] = useState<Document | null>(null);

  // Document collector state
  const [collectorOpen, setCollectorOpen] = useState(false);

  // Move to folder dialog state
  const [moveDoc, setMoveDoc] = useState<Document | null>(null);

  // Folders for breadcrumb/folder name display
  const { folders } = useFolders();

  // Auth state (prevents session race conditions on first render)
  const { user, status, isHydrated } = useAuth();
  const userId = user?.id || null;

  // Background thumbnail generation for PDFs without thumbnails
  // This silently processes documents in the background when user visits this page
  useBackgroundThumbnails(documents);

  // Determine if we're in trash view
  const isTrashView = viewParam === "trash";
  const isStarredView = viewParam === "starred";
  const isRecentView = viewParam === "recent";
  const isFolderView = !!folderParam;

  // Get folder name for title
  const currentFolder = folderParam ? folders.find((f) => f.id === folderParam) : null;

  // Dynamic page title
  const getPageTitle = () => {
    if (isTrashView) return "Corbeille";
    if (isStarredView) return "Favoris";
    if (isRecentView) return "Documents récents";
    if (currentFolder) return currentFolder.name;
    return "Documents";
  };

  // Use ref to avoid stale closure issues with translations
  const translationsRef = useRef(t);
  useEffect(() => {
    translationsRef.current = t;
  }, [t]);

  // Throttle refreshes to avoid "jumping" when lots of realtime updates arrive
  const refreshTimerRef = useRef<number | null>(null);
  const refreshInFlightRef = useRef(false);
  const fetchDocumentsRef = useRef<(showLoading?: boolean) => void>(() => {});

  const scheduleRefresh = useCallback((showLoading: boolean) => {
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
    }
    refreshTimerRef.current = window.setTimeout(() => {
      fetchDocumentsRef.current(showLoading);
    }, 300); // Reduced from 600ms for faster updates
  }, []);

  const fetchDocuments = useCallback(
    async (showLoading = true) => {
      // Don’t try to fetch until auth is settled
      if (!isHydrated || status !== "authenticated" || !userId) {
        if (showLoading) setLoading(true);
        return;
      }

      if (refreshInFlightRef.current) return;
      refreshInFlightRef.current = true;

      if (showLoading) setLoading(true);

      // Compute view flags inside callback to ensure fresh values
      const isTrash = viewParam === "trash";
      const isStarred = viewParam === "starred";
      const isRecent = viewParam === "recent";

      // Build query based on view params
      let query = supabase
        .from("documents")
        .select("*, document_taxonomy(family)")
        // Explicit user filter to avoid surprises + keeps realtime filters consistent
        .eq("user_id", userId);

      // Filter based on view type
      if (isTrash) {
        // Trash view: only show soft-deleted documents
        query = query.not("deleted_at", "is", null);
      } else {
        // All other views: exclude soft-deleted documents
        query = query.is("deleted_at", null);

        if (isStarred) {
          query = query.eq("is_starred", true);
        }

        if (folderParam) {
          query = query.eq("folder_id", folderParam);
        }
      }

      // Order by: recent view uses last_viewed_at (Google Drive style), others use created_at
      // Recent view is limited to 30 docs for performance and relevance
      const RECENT_DOCS_LIMIT = 30;
      if (isRecent) {
        query = query
          .order("last_viewed_at", { ascending: false, nullsFirst: false })
          .limit(RECENT_DOCS_LIMIT);
      } else {
        query = query.order("created_at", { ascending: false });
      }

      const { data: docs, error } = await query;

      if (error) {
        console.error("fetchDocuments error:", error);
        toast.error(translationsRef.current.documents.loadError);
        setDocuments([]);
        setLoading(false);
        refreshInFlightRef.current = false;
        return;
      }

      if (!docs || docs.length === 0) {
        setDocuments([]);
        setLoading(false);
        refreshInFlightRef.current = false;
        return;
      }

      // Fetch tag links and all tags IN PARALLEL for speed
      const docIds = docs.map((d) => d.id);
      const [tagLinksResult, allTagsResult] = await Promise.all([
        supabase
          .from("document_tag_links")
          .select("document_id, tag_id")
          .in("document_id", docIds),
        supabase
          .from("document_tags")
          .select("*")
          .eq("user_id", userId)
      ]);

      const tagLinks = tagLinksResult.data;
      const allTags = allTagsResult.data;

      if (tagLinksResult.error) console.warn("tag links fetch error:", tagLinksResult.error);
      if (allTagsResult.error) console.warn("tags fetch error:", allTagsResult.error);

      // Map tags to documents and extract taxonomy family
      const docsWithTags: DocumentWithTags[] = docs.map((doc: any) => {
        const docTagIds = tagLinks?.filter((l) => l.document_id === doc.id).map((l) => l.tag_id) || [];
        const docTags = allTags?.filter((tag) => docTagIds.includes(tag.id)) || [];
        // Extract family from joined document_taxonomy
        const taxonomy_family = doc.document_taxonomy?.family || null;
        const { document_taxonomy, ...docWithoutJoin } = doc;
        return { ...docWithoutJoin, tags: docTags, taxonomy_family };
      });

      setDocuments(docsWithTags);
      setLoading(false);
      refreshInFlightRef.current = false;
    },
    [folderParam, isHydrated, navigate, status, userId, viewParam]
  );

  useEffect(() => {
    fetchDocumentsRef.current = fetchDocuments;
  }, [fetchDocuments]);

  useEffect(() => {
    // Only fetch once we are authenticated (prevents redirect/flicker loops)
    if (status !== "authenticated" || !isHydrated || !userId) return;

    fetchDocuments(true);

    // Subscribe to realtime updates for documents (filtered to current user)
    // Listen to ALL events (INSERT, UPDATE, DELETE) for instant classification updates
    const channel = supabase
      .channel(`documents-changes-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*", // All events: INSERT, UPDATE, DELETE
          schema: "public",
          table: "documents",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          console.log("[Documents] Realtime event:", payload.eventType, payload.new);
          
          // Show toast when a specific document analysis completes
          if (payload.eventType === 'UPDATE' && payload.new) {
            const newDoc = payload.new as any;
            const oldDoc = documents.find(d => d.id === newDoc.id);
            
            // Transition from processing → complete with a valid type
            if (oldDoc?.status === 'processing' && newDoc.status === 'complete' && newDoc.doc_type && newDoc.doc_type !== 'other') {
              const typeConfig = getTypeConfig(newDoc.doc_type);
              const docTitle = newDoc.title?.length > 30 
                ? newDoc.title.substring(0, 30) + '...' 
                : newDoc.title;
              toast.success(
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">{docTitle}</span>
                  <span className="text-xs text-muted-foreground">
                    Classé comme {typeConfig.emoji} {typeConfig.label}
                  </span>
                </div>,
                { duration: 4000 }
              );
            }
          }
          
          // Fast silent refresh for instant UX when classification completes
          scheduleRefresh(false);
        }
      )
      .subscribe((status) => {
        console.log("[Documents] Realtime subscription:", status);
      });

    return () => {
      supabase.removeChannel(channel);
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [fetchDocuments, isHydrated, scheduleRefresh, status, userId]);

  // Auto-refresh when documents are processing (V2 classification in progress)
  const hasProcessingDocs = useMemo(
    () => documents.some((doc) => doc.status === "processing"),
    [documents]
  );

  useEffect(() => {
    if (!hasProcessingDocs) return;

    const intervalId = window.setInterval(() => {
      console.log("[Documents] Polling for processing docs update...");
      scheduleRefresh(false);
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [hasProcessingDocs, scheduleRefresh]);

  // Note: Auto-trigger search from URL param is now handled by PremiumAISearch internally

  // Handle inline search state changes from PremiumAISearch
  const handleSearchStateChange = useCallback((state: {
    isSearching: boolean;
    searchQuery: string;
    searchResults: SearchResult[];
    isLoading: boolean;
    totalCount: number;
    hasMore: boolean;
    isLoadingMore: boolean;
    loadMore: () => void;
  }) => {
    setInlineSearchState(state);
  }, []);

  const getKeyFields = (doc: Document) => {
    const fields = doc.key_fields as Record<string, any> | null;
    if (!fields) return { amount: null, currency: null, parties: [], reference: null };
    return {
      amount: fields.amount as number | null,
      currency: fields.currency as string | null,
      parties: (fields.parties || []) as string[],
      reference: fields.reference as string | null,
    };
  };

  // Check if document is expiring soon (within 30 days)
  const getExpirationStatus = (doc: Document) => {
    if (!doc.expires_at || !isExpirableDocType(doc.doc_type)) return null;
    
    const expirationDate = new Date(doc.expires_at);
    const today = new Date();
    const diffTime = expirationDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return { status: "expired", days: Math.abs(diffDays), label: `Expiré depuis ${Math.abs(diffDays)} jour(s)` };
    if (diffDays === 0) return { status: "today", days: 0, label: "Expire aujourd'hui" };
    if (diffDays <= 7) return { status: "urgent", days: diffDays, label: `Expire dans ${diffDays} jour(s)` };
    if (diffDays <= 30) return { status: "warning", days: diffDays, label: `Expire dans ${diffDays} jour(s)` };
    return null;
  };

  const isDocumentExpiring = (doc: Document) => {
    return getExpirationStatus(doc) !== null;
  };

  // Note: When inline search is active, we show InlineSearchResults instead of filteredDocs
  const filteredDocs: DocumentWithTags[] = documents.filter((doc) => {
    const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === "all" || doc.doc_type === typeFilter;
    
    // Family filter (hybrid: use taxonomy_family first, fallback to category->family)
    const docFamily = doc.taxonomy_family || getFamilyFromCategory(doc.category);
    const matchesFamily = familyFilter === "all" || docFamily === familyFilter;
    
    const matchesSource = sourceFilter === "all" || doc.source === sourceFilter;
    
    // Amount filter
    const keyFields = getKeyFields(doc);
    const minAmount = parseFloat(minAmountFilter);
    const matchesAmount = !minAmountFilter || (keyFields.amount !== null && keyFields.amount >= minAmount);
    
    // Party filter
    const matchesParty = !partyFilter || keyFields.parties.some(
      party => party.toLowerCase().includes(partyFilter.toLowerCase())
    );

    // Expiring filter
    const matchesExpiring = !expiringFilter || isDocumentExpiring(doc);
    
    return matchesSearch && matchesType && matchesFamily && matchesSource && matchesAmount && matchesParty && matchesExpiring;
  });

  // Count documents being analyzed
  const analyzingCount = useMemo(() => 
    documents.filter(doc => isDocumentAnalyzing(doc.doc_type, doc.status)).length,
    [documents]
  );
  
  // Track previous analyzing count for "all done" toast
  const prevAnalyzingCount = useRef(0);
  
  useEffect(() => {
    if (prevAnalyzingCount.current > 0 && analyzingCount === 0) {
      toast.success("Tous les documents ont été analysés", {
        icon: "🎉",
        duration: 4000
      });
    }
    prevAnalyzingCount.current = analyzingCount;
  }, [analyzingCount]);

  // Sort documents
  const sortedDocs = useMemo(() => {
    const docs = [...filteredDocs];
    switch (sortBy) {
      case "date_desc": 
        return docs.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      case "date_asc": 
        return docs.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      case "name_asc": 
        return docs.sort((a, b) => a.title.localeCompare(b.title));
      case "name_desc": 
        return docs.sort((a, b) => b.title.localeCompare(a.title));
      case "type": 
        return docs.sort((a, b) => (a.doc_type || "zzz").localeCompare(b.doc_type || "zzz"));
      default: 
        return docs;
    }
  }, [filteredDocs, sortBy]);

  // Pagination calculations
  const totalPages = Math.ceil(sortedDocs.length / docsPerPage);
  const paginatedDocs = useMemo(() => {
    const startIndex = (currentPage - 1) * docsPerPage;
    return sortedDocs.slice(startIndex, startIndex + docsPerPage);
  }, [sortedDocs, currentPage, docsPerPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, typeFilter, familyFilter, sourceFilter, minAmountFilter, partyFilter, expiringFilter, inlineSearchState.isSearching, docsPerPage]);

  // Persist view preferences to localStorage
  useEffect(() => {
    localStorage.setItem('documents_viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('documents_docsPerPage', docsPerPage.toString());
  }, [docsPerPage]);

  // Selection helpers
  const toggleDocSelection = (docId: string) => {
    setSelectedDocs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  const selectAllOnPage = () => {
    const allIds = new Set(paginatedDocs.map(doc => doc.id));
    setSelectedDocs(allIds);
  };

  const clearSelection = () => {
    setSelectedDocs(new Set());
    setSelectionMode(false);
  };

  // Open delete confirmation dialog
  const openDeleteConfirm = () => {
    if (selectedDocs.size === 0) return;
    setDeleteConfirmOpen(true);
  };

  // Bulk delete (after confirmation) - uses soft delete for normal views, hard delete for trash
  const handleBulkDelete = async () => {
    if (selectedDocs.size === 0) return;
    
    setDeleting(true);
    const docsToDelete = documents.filter(doc => selectedDocs.has(doc.id));

    if (isTrashView) {
      // Hard delete from trash
      const filePaths = docsToDelete.map(doc => doc.file_path);
      await supabase.storage.from("documents").remove(filePaths);

      const { error: dbError } = await supabase
        .from("documents")
        .delete()
        .in("id", Array.from(selectedDocs));

      if (dbError) {
        toast.error(t.documents.toasts.deleteError);
        setDeleting(false);
        return;
      }

      toast.success(`${selectedDocs.size} document(s) supprimé(s) définitivement`);
    } else {
      // Soft delete - move to trash
      const { error: dbError } = await supabase
        .from("documents")
        .update({ deleted_at: new Date().toISOString() })
        .in("id", Array.from(selectedDocs));

      if (dbError) {
        toast.error(t.documents.toasts.deleteError);
        setDeleting(false);
        return;
      }

      toast.success(`${selectedDocs.size} document(s) déplacé(s) vers la corbeille`);
    }

    setDeleteConfirmOpen(false);
    setDeleting(false);
    clearSelection();
    fetchDocuments();
  };

  // Find duplicates (exact match heuristic)
  // Priority (source_url is NO LONGER used - it caused Gmail false negatives):
  // 1) Same file_hash (exact binary match - strongest signal)
  // 2) Same content_fingerprint (OCR/text fingerprint)
  // 3) Same file_size + extracted_text hash (good for OCR'd docs)
  // 4) Same normalized file_name + file_size (fallback)
  const findDuplicates = () => {
    const hashString = (value: string) => {
      // djb2 (fast, deterministic)
      let hash = 5381;
      for (let i = 0; i < value.length; i++) {
        hash = (hash * 33) ^ value.charCodeAt(i);
      }
      // force unsigned
      return (hash >>> 0).toString(16);
    };

    // Normalize title for comparison (remove accents, lowercase, remove extra spaces)
    const normalizeTitle = (title: string) => {
      return title
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // Remove accents
        .replace(/[^a-z0-9]/g, " ") // Keep only alphanumeric
        .replace(/\s+/g, " ")
        .trim();
    };

    const buildDuplicateKey = (doc: DocumentWithTags) => {
      const size = doc.file_size ?? null;
      
      // Priority 1: Use file_hash if available (exact binary match)
      const fileHash = (doc as any).file_hash;
      if (fileHash) {
        return `hash:${fileHash}`;
      }
      
      // Priority 2: Use content_fingerprint if available
      const fingerprint = (doc as any).content_fingerprint;
      if (fingerprint) {
        return `fp:${fingerprint}`;
      }
      
      // Priority 3: file_size + extracted_text hash (good for OCR'd docs)
      const text = (doc.extracted_text || "").trim();
      if (size && text.length >= 50) {
        return `text:${size}:${hashString(text)}`;
      }
      
      // Priority 4: Same title (normalized) + similar file_size (within 1% tolerance)
      // This catches cases where same document imported from different sources
      // (e.g., Powens vs Gmail) may have slight binary differences
      const normalizedTitle = normalizeTitle(doc.title);
      if (normalizedTitle.length >= 10 && size) {
        // Round size to nearest 1KB for tolerance
        const sizeRounded = Math.round(size / 1024);
        return `title:${normalizedTitle}:${sizeRounded}`;
      }
      
      // Priority 5: Fallback to normalized_name + size
      const normalizedName = doc.file_name.toLowerCase().trim();
      return `name:${normalizedName}:${size ?? ""}`;
    };

    // Build groups using ONLY reliable criteria (hash, fingerprint, text content)
    // REMOVED: size-based grouping which caused massive false positives
    // (e.g., grouping different invoices just because they had similar file sizes)
    const groupsMap = new Map<string, DocumentWithTags[]>();

    documents.forEach((doc) => {
      const key = buildDuplicateKey(doc);
      const existing = groupsMap.get(key) || [];
      existing.push(doc);
      groupsMap.set(key, existing);
    });

    const groups: DuplicateGroup[] = [];
    
    groupsMap.forEach((docs, key) => {
      if (docs.length > 1) {
        docs.sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        );
        groups.push({ key, documents: docs });
      }
    });

    if (groups.length === 0) {
      toast.info(t.documents.toasts.noDuplicates);
      return;
    }

    setDuplicateGroups(groups);
    // Pre-select all duplicates EXCEPT the first (oldest) of each group
    const toSelect = new Set<string>();
    groups.forEach((group) => {
      group.documents.slice(1).forEach((doc) => toSelect.add(doc.id));
    });
    setSelectedDuplicates(toSelect);
    setDuplicatesDialogOpen(true);
  };

  // Delete selected duplicates
  const handleDeleteDuplicates = async () => {
    if (selectedDuplicates.size === 0) {
      toast.info(t.documents.toasts.noSelected);
      return;
    }

    setDeletingDuplicates(true);
    
    const docsToDelete = documents.filter(doc => selectedDuplicates.has(doc.id));
    const filePaths = docsToDelete.map(doc => doc.file_path);

    // Soft delete - move to trash instead of permanent deletion
    const { error: dbError } = await supabase
      .from("documents")
      .update({ deleted_at: new Date().toISOString() })
      .in("id", Array.from(selectedDuplicates));

    if (dbError) {
      toast.error(t.documents.toasts.deleteError);
      setDeletingDuplicates(false);
      return;
    }

    toast.success(`${selectedDuplicates.size} ${t.documents.toasts.duplicatesDeleted}`);
    setDuplicatesDialogOpen(false);
    setSelectedDuplicates(new Set());
    setDuplicateGroups([]);
    setDeletingDuplicates(false);
    fetchDocuments();
  };

  const getTypeLabel = (type: string) => {
    const config = getTypeConfig(type);
    return `${config.emoji} ${config.label}`;
  };

  // Helper to get family from doc (hybrid: taxonomy_family first, then category fallback)
  const getDocFamilyLabel = (doc: DocumentWithTags) => {
    const family = doc.taxonomy_family || getFamilyFromCategory(doc.category);
    const config = getFamilyConfig(family);
    return `${config.emoji} ${config.label}`;
  };

  const getSourceLabel = (source: string) => {
    const labels: Record<string, string> = {
      gmail: t.documents.sources.gmail,
      outlook: t.documents.sources.outlook,
      whatsapp: t.documents.sources.whatsapp,
      upload: t.documents.sources.upload,
    };
    return labels[source] || source;
  };

  const getSourceVariant = (source: string) => {
    const variants: Record<string, any> = {
      gmail: "gmail", outlook: "outlook", whatsapp: "whatsapp", upload: "upload"
    };
    return variants[source] || "upload";
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Format last viewed time for "Récents" view
  const formatLastViewed = (dateStr: string | null): string | null => {
    if (!dateStr) return null;
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: fr });
  };

  const formatSize = (bytes: number | null) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  const formatAmount = (amount: number | null | undefined, currency: string | null | undefined) => {
    if (!amount) return null;
    const validCurrencyCodes = ["EUR", "USD", "GBP", "CHF", "CAD", "AUD", "JPY"];
    const cur = currency && validCurrencyCodes.includes(currency.toUpperCase()) 
      ? currency.toUpperCase() 
      : "EUR";
    try {
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: cur,
      }).format(amount);
    } catch {
      return `${amount.toFixed(2)} €`;
    }
  };

  // Soft delete - move to trash
  const handleDelete = async (doc: Document, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const { error: dbError } = await supabase
      .from("documents")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", doc.id);

    if (dbError) {
      toast.error(t.documents.toasts.deleteError);
      return;
    }

    toast.success("Document déplacé vers la corbeille");
    fetchDocuments();
  };

  // Restore from trash
  const handleRestore = async (doc: Document, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const { error: dbError } = await supabase
      .from("documents")
      .update({ deleted_at: null })
      .eq("id", doc.id);

    if (dbError) {
      toast.error("Erreur lors de la restauration");
      return;
    }

    toast.success("Document restauré");
    fetchDocuments();
  };

  // Permanent delete (for trash view)
  const handlePermanentDelete = async (doc: Document, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Delete from storage first
    const { error: storageError } = await supabase.storage
      .from("documents")
      .remove([doc.file_path]);

    if (storageError) {
      console.warn("Storage delete error:", storageError);
      // Continue anyway - file might already be deleted
    }

    const { error: dbError } = await supabase
      .from("documents")
      .delete()
      .eq("id", doc.id);

    if (dbError) {
      toast.error(t.documents.toasts.deleteError);
      return;
    }

    toast.success("Document supprimé définitivement");
    fetchDocuments();
  };

  // Toggle starred status
  const handleToggleStar = async (doc: Document, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const newStarred = !doc.is_starred;
    const { error } = await supabase
      .from("documents")
      .update({ is_starred: newStarred })
      .eq("id", doc.id);

    if (error) {
      toast.error("Erreur lors de la mise à jour");
      return;
    }

    toast.success(newStarred ? "Ajouté aux favoris" : "Retiré des favoris");
    fetchDocuments();
  };

  // Move document to folder
  const handleMoveToFolder = async (folderId: string | null) => {
    if (!moveDoc) return;
    
    const { error } = await supabase
      .from("documents")
      .update({ folder_id: folderId })
      .eq("id", moveDoc.id);

    if (error) {
      toast.error("Erreur lors du déplacement");
      throw error;
    }

    toast.success(folderId ? "Document déplacé" : "Document retiré du dossier");
    setMoveDoc(null);
    fetchDocuments();
  };

  // Empty trash (permanent delete all)
  const handleEmptyTrash = async () => {
    const trashDocs = documents.filter(doc => doc.deleted_at);
    if (trashDocs.length === 0) return;

    setDeleting(true);
    
    // Delete all files from storage
    const filePaths = trashDocs.map(doc => doc.file_path);
    await supabase.storage.from("documents").remove(filePaths);

    // Delete all from database
    const { error } = await supabase
      .from("documents")
      .delete()
      .not("deleted_at", "is", null);

    if (error) {
      toast.error("Erreur lors du vidage de la corbeille");
      setDeleting(false);
      return;
    }

    toast.success("Corbeille vidée");
    setDeleting(false);
    fetchDocuments();
  };

  const handleDownload = async (doc: Document, e: React.MouseEvent) => {
    e.stopPropagation();

    const { data, error } = await supabase.storage
      .from("documents")
      .download(doc.file_path);

    if (error) {
      toast.error(t.documents.toasts.downloadError);
      return;
    }

    const url = URL.createObjectURL(data);
    const a = window.document.createElement("a");
    a.href = url;
    a.download = doc.file_name;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleRetryOCR = async (doc: Document, e: React.MouseEvent) => {
    e.stopPropagation();
    
    toast.info(t.documents.toasts.extracting);
    
    // Set status to processing
    await supabase
      .from("documents")
      .update({ status: 'processing' })
      .eq("id", doc.id);
    
    fetchDocuments();

    try {
      const { data: extractedInfo, error: extractError } = await supabase.functions.invoke('extract-document-info', {
        body: {
          fileName: doc.file_name,
          mimeType: doc.mime_type,
          docType: doc.doc_type,
          filePath: doc.file_path,
        }
      });

      if (extractError) {
        console.error('OCR extraction error:', extractError);
        toast.error(t.documents.toasts.extractError);
        await supabase
          .from("documents")
          .update({ status: 'complete' })
          .eq("id", doc.id);
      } else if (extractedInfo) {
        await supabase
          .from("documents")
          .update({
            extracted_text: extractedInfo.extracted_text,
            ai_summary: extractedInfo.summary,
            doc_date: extractedInfo.doc_date,
            expires_at: extractedInfo.expires_at,
            key_fields: {
              amount: extractedInfo.amount,
              currency: extractedInfo.currency,
              parties: extractedInfo.parties,
              reference: extractedInfo.reference,
            },
            status: 'complete',
          })
          .eq("id", doc.id);
        
        toast.success(t.documents.toasts.extractSuccess);
      }
    } catch (err) {
      console.error('OCR error:', err);
      toast.error(t.documents.toasts.extractOcrError);
      await supabase
        .from("documents")
        .update({ status: 'complete' })
        .eq("id", doc.id);
    }
    
    fetchDocuments();
  };

  return (
    <div className="min-h-screen bg-background py-6 md:py-8">
      <SEO 
        title={getPageTitle()}
        description="Gérez tous vos documents : factures, contrats, attestations. Recherche IA, filtres avancés et partage sécurisé."
        noIndex={true}
      />
      
      {/* Header - dynamic based on view */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {/* Only show back button on sub-views (folder, trash, starred, recent) */}
          {(isFolderView || isTrashView || isStarredView || isRecentView) && (
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate("/documents")} 
              className="text-muted-foreground md:hidden"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          <div>
            <h1 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
              {isTrashView && <Trash className="w-5 h-5 text-muted-foreground" />}
              {isStarredView && <Star className="w-5 h-5 text-warning fill-warning" />}
              {isFolderView && <FolderIcon className="w-5 h-5" style={{ color: currentFolder?.color || "#6366f1" }} />}
              {getPageTitle()}
            </h1>
            <p className="text-xs text-muted-foreground">
              {filteredDocs.length} document{filteredDocs.length > 1 ? 's' : ''}
            </p>
          </div>
        </div>
        
        {/* Desktop actions - context-aware */}
        <div className="hidden md:flex items-center gap-2">
          {isTrashView ? (
            <Button 
              variant="destructive" 
              size="sm"
              onClick={handleEmptyTrash}
              disabled={deleting || documents.length === 0}
            >
              {deleting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              <Trash2 className="w-4 h-4 mr-1.5" />
              Vider la corbeille
            </Button>
          ) : (
            <>
              <Button 
                variant="ghost" 
                size="sm"
                className="text-muted-foreground hover:text-foreground" 
                onClick={() => setCollectorOpen(true)}
              >
                <FolderOpen className="w-4 h-4 mr-1.5" />
                Dossier
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                className="text-muted-foreground hover:text-foreground" 
                onClick={() => navigate("/pdf-tools?tab=scanner")}
              >
                <ScanLine className="w-4 h-4 mr-1.5" />
                Scanner
              </Button>
              <Button 
                size="sm"
                onClick={() => setUploadOpen(true)}
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Importer
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Breadcrumb for folder navigation */}
      {isFolderView && (
        <FolderBreadcrumb currentFolderId={folderParam} className="mb-4" />
      )}

      <div className="space-y-4">
        {/* Premium AI Search + Actions Badge - Unified Home (hide in trash view) */}
        {!isTrashView && !isFolderView && !isStarredView && !isRecentView && (
          <>
            <PremiumAISearch 
              onDocumentClick={(doc) => openDocument(doc)} 
              onSearchStateChange={handleSearchStateChange}
            />
            {!inlineSearchState.isSearching && <ActionsBadge className="mb-4" />}
          </>
        )}

        {/* Mobile Quick Actions - compact */}
        <div className="flex gap-2 mb-4 md:hidden">
          <Button 
            variant="outline"
            size="sm"
            className="flex-1 h-9"
            onClick={() => setCollectorOpen(true)}
          >
            <FolderOpen className="w-4 h-4 mr-1.5" />
            Dossier
          </Button>
          <Button 
            variant="outline"
            size="sm"
            className="flex-1 h-9"
            onClick={() => navigate("/pdf-tools?tab=scanner")}
          >
            <ScanLine className="w-4 h-4 mr-1.5" />
            Scanner
          </Button>
          <Button 
            size="sm"
            className="flex-1 h-9"
            onClick={() => setUploadOpen(true)}
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Importer
          </Button>
        </div>

        {/* Filters - minimal, horizontal (hide when searching) */}
        {!inlineSearchState.isSearching && (
          <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
            <Button
              variant={expiringFilter ? "destructive" : "ghost"}
              size="sm"
              onClick={() => setExpiringFilter(!expiringFilter)}
              className="shrink-0 h-8 text-xs"
            >
              <AlertTriangle className="w-3.5 h-3.5 mr-1" />
              Alertes
              {expiringFilter && documents.filter(isDocumentExpiring).length > 0 && (
                <span className="ml-1.5 text-[10px] bg-destructive-foreground/20 px-1.5 rounded">
                  {documents.filter(isDocumentExpiring).length}
                </span>
              )}
            </Button>
            
            <div className="h-4 w-px bg-border" />
            
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[110px] h-8 text-xs border-0 bg-transparent hover:bg-muted">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                {Object.entries(TYPE_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.emoji} {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={familyFilter} onValueChange={setFamilyFilter}>
              <SelectTrigger className="w-[110px] h-8 text-xs border-0 bg-transparent hover:bg-muted">
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {Object.entries(FAMILY_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.emoji} {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex-1" />
            
            {/* Sort selector */}
            <Select value={sortBy} onValueChange={(val) => setSortBy(val as SortOption)}>
              <SelectTrigger className="w-[120px] h-8 text-xs shrink-0">
                <SelectValue placeholder="Trier par" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date_desc">Plus récent</SelectItem>
                <SelectItem value="date_asc">Plus ancien</SelectItem>
                <SelectItem value="name_asc">Nom A→Z</SelectItem>
                <SelectItem value="name_desc">Nom Z→A</SelectItem>
                <SelectItem value="type">Par type</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Density selector */}
            <Select 
              value={docsPerPage.toString()} 
              onValueChange={(val) => {
                setDocsPerPage(Number(val));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-[85px] h-8 text-xs shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DENSITY_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option.toString()}>
                    {option} / page
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex border border-border rounded-md overflow-hidden shrink-0">
              <Button
                variant={viewMode === "compact" ? "secondary" : "ghost"}
                size="icon"
                className="h-8 w-8 rounded-none"
                onClick={() => setViewMode("compact")}
                title="Vue compacte"
              >
                <List className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="icon"
                className="h-8 w-8 rounded-none"
                onClick={() => setViewMode("list")}
                title="Vue liste"
              >
                <FileText className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant={viewMode === "grid" ? "secondary" : "ghost"}
                size="icon"
                className="h-8 w-8 rounded-none"
                onClick={() => setViewMode("grid")}
                title="Vue grille"
              >
                <Grid className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}

        {/* Document count and options bar (hide when searching) */}
        {!inlineSearchState.isSearching && (
          <div className="flex items-center justify-between mb-3 text-xs">
            <div className="flex items-center gap-2">
              <p className="text-muted-foreground">
                {filteredDocs.length} document{filteredDocs.length > 1 ? 's' : ''}
                {totalPages > 1 && ` · Page ${currentPage}/${totalPages}`}
              </p>
              <AnalysisProgressBar 
                analyzingCount={analyzingCount} 
                totalRecentDocs={documents.filter(d => 
                  new Date(d.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000)
                ).length || analyzingCount}
              />
            </div>
            
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={findDuplicates}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Doublons
              </button>
              
              {!selectionMode ? (
                <button
                  onClick={() => setSelectionMode(true)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  Sélectionner
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <button onClick={selectAllOnPage} className="text-muted-foreground hover:text-foreground">
                    Tout
                  </button>
                  <button onClick={clearSelection} className="text-muted-foreground hover:text-foreground">
                    Annuler
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Selection action bar - subtle */}
        <AnimatePresence>
          {selectedDocs.size > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-3 p-3 bg-destructive/5 border border-destructive/10 rounded-md flex items-center justify-between"
            >
              <span className="text-sm text-destructive">
                {selectedDocs.size} sélectionné{selectedDocs.size > 1 ? 's' : ''}
              </span>
              <Button
                variant="destructive"
                size="sm"
                onClick={openDeleteConfirm}
                className="h-7 text-xs"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Supprimer
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Inline Search Results - shows when search is active */}
        {inlineSearchState.isSearching ? (
          <InlineSearchResults
            searchQuery={inlineSearchState.searchQuery}
            searchResults={inlineSearchState.searchResults}
            isLoading={inlineSearchState.isLoading}
            onDocumentClick={(doc) => openDocument(doc)}
            totalCount={inlineSearchState.totalCount}
            hasMore={inlineSearchState.hasMore}
            onLoadMore={inlineSearchState.loadMore}
            isLoadingMore={inlineSearchState.isLoadingMore}
          />
        ) : loading ? (
          viewMode === "grid" ? (
            <SkeletonDocumentsGrid count={6} />
          ) : (
            <SkeletonDocumentsList count={viewMode === "compact" ? 12 : 8} />
          )
        ) : filteredDocs.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground mb-4">{t.documents.noResults}</p>
            <Button variant="outline" size="sm" onClick={() => setUploadOpen(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Importer
            </Button>
          </div>
        ) : viewMode === "compact" ? (
          /* COMPACT VIEW - Maximum density for power users */
          <div className="border border-border rounded-md overflow-hidden bg-card">
            {paginatedDocs.map((doc: any) => {
              const keyFields = getKeyFields(doc);
              const formattedAmount = formatAmount(keyFields.amount, keyFields.currency);
              const typeEmoji = getDocumentTypeEmoji(doc.doc_type, doc.status);
              const typeLabel = getDocumentTypeLabel(doc.doc_type, doc.status, false);
              const analyzing = isDocumentAnalyzing(doc.doc_type, doc.status);
              const isSelected = selectedDocs.has(doc.id);
              const expirationStatus = getExpirationStatus(doc);
              
              return (
                <DraggableDocument
                  key={doc.id}
                  documentId={doc.id}
                  documentTitle={doc.title}
                  disabled={selectionMode || isTrashView}
                >
                  <div
                    className={`doc-row-compact flex items-center gap-2 cursor-pointer group ${
                      isSelected ? 'bg-primary/5' : ''
                    }`}
                    onClick={() => selectionMode ? toggleDocSelection(doc.id) : openDocument(doc)}
                  >
                  {selectionMode && (
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleDocSelection(doc.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-3.5 w-3.5"
                    />
                  )}
                  
                  {/* Tiny type indicator */}
                  <span className={`text-xs shrink-0 w-5 text-center ${analyzing ? 'animate-pulse' : ''}`}>{typeEmoji}</span>
                  
                  {/* Title - primary */}
                  <p className="text-[13px] font-medium text-foreground truncate flex-1 min-w-0">{doc.title}</p>
                  
                  {/* Inline metadata - very subtle */}
                  <span className={`text-[11px] shrink-0 hidden sm:block ${analyzing ? 'text-primary/70 animate-pulse' : 'text-muted-foreground/70'}`}>{typeLabel}</span>
                  {/* Show last viewed in Recent view, otherwise show updated_at */}
                  {isRecentView && doc.last_viewed_at ? (
                    <span className="text-[11px] text-primary/70 shrink-0 flex items-center gap-0.5">
                      <Eye className="w-3 h-3" />
                      {formatLastViewed(doc.last_viewed_at)}
                    </span>
                  ) : (
                    <span className="text-[11px] text-muted-foreground/50 shrink-0">{formatDate(doc.updated_at)}</span>
                  )}
                  {formattedAmount && (
                    <span className="text-[11px] font-medium text-foreground shrink-0">{formattedAmount}</span>
                  )}
                  
                  {/* Expiration - minimal */}
                  {expirationStatus && (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded shrink-0 ${
                      expirationStatus.status === 'expired' ? 'bg-destructive/10 text-destructive' :
                      expirationStatus.status === 'urgent' || expirationStatus.status === 'today' ? 'bg-warning/10 text-warning' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {expirationStatus.label}
                    </span>
                  )}
                  
                  {/* Actions on hover */}
                  {!selectionMode && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity">
                          <MoreHorizontal className="w-3.5 h-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {isTrashView ? (
                          <>
                            <DropdownMenuItem onClick={(e) => handleRestore(doc, e)}>
                              <RotateCcw className="w-4 h-4 mr-2" />
                              Restaurer
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => handlePermanentDelete(doc, e)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Supprimer définitivement
                            </DropdownMenuItem>
                          </>
                        ) : (
                          <>
                            <DropdownMenuItem onClick={(e) => handleToggleStar(doc, e)}>
                              <Star className={`w-4 h-4 mr-2 ${doc.is_starred ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                              {doc.is_starred ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setMoveDoc(doc); }}>
                              <FolderIcon className="w-4 h-4 mr-2" />
                              Déplacer vers...
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setShareDoc(doc); }}>
                              <Share2 className="w-4 h-4 mr-2" />
                              Partager
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditDoc(doc); }}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Modifier
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => handleDownload(doc, e)}>
                              <Download className="w-4 h-4 mr-2" />
                              Télécharger
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={(e) => handleDelete(doc, e)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  </div>
                </DraggableDocument>
              );
            })}
          </div>
        ) : viewMode === "list" ? (
          /* LIST VIEW - Balanced density */
          <div className="border border-border rounded-md overflow-hidden bg-card">
            {paginatedDocs.map((doc: any) => {
              const keyFields = getKeyFields(doc);
              const formattedAmount = formatAmount(keyFields.amount, keyFields.currency);
              const typeEmoji = getDocumentTypeEmoji(doc.doc_type, doc.status);
              const typeLabel = getDocumentTypeLabel(doc.doc_type, doc.status, false);
              const analyzing = isDocumentAnalyzing(doc.doc_type, doc.status);
              const isSelected = selectedDocs.has(doc.id);
              const expirationStatus = getExpirationStatus(doc);
              
              return (
                <DraggableDocument
                  key={doc.id}
                  documentId={doc.id}
                  documentTitle={doc.title}
                  disabled={selectionMode || isTrashView}
                >
                  <div
                    className={`doc-row flex items-center gap-3 cursor-pointer group ${
                      isSelected ? 'bg-primary/5' : ''
                    }`}
                    onClick={() => selectionMode ? toggleDocSelection(doc.id) : openDocument(doc)}
                  >
                  {selectionMode && (
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleDocSelection(doc.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  
                  {/* Type icon - small, subtle */}
                  <div className={`w-7 h-7 rounded bg-muted flex items-center justify-center text-sm shrink-0 ${analyzing ? 'animate-pulse' : ''}`}>
                    {typeEmoji}
                  </div>
                  
                  {/* Main content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-[13px] font-medium text-foreground truncate">{doc.title}</p>
                      {analyzing && (
                        <Loader2 className="w-3 h-3 animate-spin text-muted-foreground shrink-0" />
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className={`text-[11px] ${analyzing ? 'text-primary/70 animate-pulse' : 'text-muted-foreground'}`}>{typeLabel}</span>
                      <span className="text-muted-foreground/30">·</span>
                      {/* Show last viewed in Recent view, otherwise show updated_at */}
                      {isRecentView && doc.last_viewed_at ? (
                        <span className="text-[11px] text-primary/70 flex items-center gap-0.5">
                          <Eye className="w-3 h-3" />
                          {formatLastViewed(doc.last_viewed_at)}
                        </span>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">{formatDate(doc.updated_at)}</span>
                      )}
                      {formattedAmount && (
                        <>
                          <span className="text-muted-foreground/30">·</span>
                          <span className="text-[11px] font-medium text-foreground">{formattedAmount}</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Expiration badge - only when relevant */}
                  {expirationStatus && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${
                      expirationStatus.status === 'expired' ? 'bg-destructive/10 text-destructive' :
                      expirationStatus.status === 'urgent' || expirationStatus.status === 'today' ? 'bg-warning/10 text-warning' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {expirationStatus.label}
                    </span>
                  )}
                  
                  {/* Actions - subtle, appear on hover */}
                  {!selectionMode && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {isTrashView ? (
                          <>
                            <DropdownMenuItem onClick={(e) => handleRestore(doc, e)}>
                              <RotateCcw className="w-4 h-4 mr-2" />
                              Restaurer
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => handlePermanentDelete(doc, e)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Supprimer définitivement
                            </DropdownMenuItem>
                          </>
                        ) : (
                          <>
                            <DropdownMenuItem onClick={(e) => handleToggleStar(doc, e)}>
                              <Star className={`w-4 h-4 mr-2 ${doc.is_starred ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                              {doc.is_starred ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setMoveDoc(doc); }}>
                              <FolderIcon className="w-4 h-4 mr-2" />
                              Déplacer vers...
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setShareDoc(doc); }}>
                              <Share2 className="w-4 h-4 mr-2" />
                              Partager
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditDoc(doc); }}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Modifier
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => handleDownload(doc, e)}>
                              <Download className="w-4 h-4 mr-2" />
                              Télécharger
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={(e) => handleDelete(doc, e)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  </div>
                </DraggableDocument>
              );
            })}
          </div>
        ) : (
          /* GRID VIEW - Compact cards */
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
            {paginatedDocs.map((doc: any) => {
              const typeLabel = getDocumentTypeLabel(doc.doc_type, doc.status, false);
              const analyzing = isDocumentAnalyzing(doc.doc_type, doc.status);
              const isSelected = selectedDocs.has(doc.id);
              
              return (
                <DraggableDocument
                  key={doc.id}
                  documentId={doc.id}
                  documentTitle={doc.title}
                  disabled={selectionMode || isTrashView}
                >
                  <div
                    className={`p-2.5 border border-border rounded-md bg-card cursor-pointer hover:bg-muted/30 transition-colors ${
                      isSelected ? 'ring-1 ring-primary bg-primary/5' : ''
                    }`}
                    onClick={() => selectionMode ? toggleDocSelection(doc.id) : openDocument(doc)}
                  >
                    {selectionMode && (
                      <div className="mb-1.5">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleDocSelection(doc.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-3.5 w-3.5"
                        />
                      </div>
                    )}
                    <div className="relative w-full aspect-[4/3] rounded overflow-hidden bg-muted mb-1.5">
                      <DocumentThumbnail doc={doc} className="w-full h-full" />
                      {doc.is_starred && (
                        <Star className="absolute top-1 right-1 w-3 h-3 fill-warning text-warning" />
                      )}
                      {/* Analyzing overlay */}
                      {analyzing && (
                        <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
                          <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        </div>
                      )}
                    </div>
                    <p className="text-[13px] font-medium truncate text-foreground">{doc.title}</p>
                    {/* Show last viewed in Recent view, otherwise show type label */}
                    {isRecentView && doc.last_viewed_at ? (
                      <p className="text-[11px] text-primary/70 mt-0.5 flex items-center gap-0.5">
                        <Eye className="w-3 h-3" />
                        {formatLastViewed(doc.last_viewed_at)}
                      </p>
                    ) : (
                      <p className={`text-[11px] mt-0.5 ${analyzing ? 'text-primary/70 animate-pulse' : 'text-muted-foreground'}`}>{typeLabel}</p>
                    )}
                  </div>
                </DraggableDocument>
              );
            })}
          </div>
        )}

        {/* Pagination controls (hide when searching) */}
        {!inlineSearchState.isSearching && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    className="w-8 h-8 p-0"
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>


      {/* Upload Dialog */}
      <DocumentUpload
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUploadComplete={fetchDocuments}
      />

      {/* Share Dialog */}
      <ShareDialog
        document={shareDoc}
        open={!!shareDoc}
        onOpenChange={(open) => !open && setShareDoc(null)}
      />

      {/* Tag Manager Dialog */}
      <TagManager
        document={tagDoc}
        open={!!tagDoc}
        onOpenChange={(open) => !open && setTagDoc(null)}
        onTagsUpdated={fetchDocuments}
      />

      {/* Edit Document Dialog */}
      <DocumentEdit
        document={editDoc}
        open={!!editDoc}
        onOpenChange={(open) => !open && setEditDoc(null)}
        onSave={fetchDocuments}
      />

      {/* Duplicates Cleanup Dialog */}
      <Dialog open={duplicatesDialogOpen} onOpenChange={setDuplicatesDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5" />
              Nettoyer les doublons
            </DialogTitle>
            <DialogDescription>
              {duplicateGroups.length} groupe(s) de doublons détectés. Le document le plus ancien de chaque groupe est conservé par défaut.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 my-4">
            {duplicateGroups.map((group) => (
              <div key={group.key} className="border border-border rounded-lg p-3">
                <p className="text-sm font-medium mb-2 text-muted-foreground">
                  {(() => {
                    const key = group.key;
                    if (key.startsWith("name:")) {
                      const parts = key.split(":");
                      return `Fichier : ${parts[1]}`;
                    }
                    if (key.startsWith("srcurl:")) {
                      return "Doublons détectés via la source (même email / import)";
                    }
                    if (key.startsWith("text:")) {
                      return "Doublons détectés par contenu (texte identique)";
                    }
                    return `Groupe : ${key}`;
                  })()}
                </p>
                <div className="space-y-2">
                  {group.documents.map((doc, idx) => {
                    const isFirst = idx === 0;
                    const isSelected = selectedDuplicates.has(doc.id);
                    const typeConfig = getTypeConfig(doc.doc_type);
                    return (
                      <div
                        key={doc.id}
                        className={`flex items-center gap-3 p-2 rounded-lg ${isFirst ? 'bg-green-500/10 border border-green-500/20' : 'bg-muted/50'}`}
                      >
                        {!isFirst && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => {
                              setSelectedDuplicates(prev => {
                                const newSet = new Set(prev);
                                if (checked) {
                                  newSet.add(doc.id);
                                } else {
                                  newSet.delete(doc.id);
                                }
                                return newSet;
                              });
                            }}
                          />
                        )}
                        {isFirst && (
                          <Badge variant="outline" className="bg-green-500/10 text-green-700 border-green-500/30 shrink-0">
                            Conserver
                          </Badge>
                        )}
                        <div className="w-8 h-8 rounded bg-muted flex items-center justify-center text-base shrink-0">
                          {typeConfig.emoji}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{doc.title}</p>
                          <p className="text-xs text-muted-foreground">
                            Créé le {formatDate(doc.created_at)} • {formatSize(doc.file_size)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={() => setDuplicatePreviewDoc(doc)}
                          title="Aperçu rapide"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {!isFirst && isSelected && (
                          <Badge variant="destructive" className="shrink-0">
                            À supprimer
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicatesDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteDuplicates}
              disabled={deletingDuplicates || selectedDuplicates.size === 0}
            >
              {deletingDuplicates ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Supprimer {selectedDuplicates.size} doublon(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Preview - Mobile uses full-screen viewer, Desktop uses Dialog */}
      {isMobile ? (
        <DocumentPreviewSheet
          document={quickPreviewDoc}
          open={!!quickPreviewDoc}
          onOpenChange={(open) => !open && setQuickPreviewDoc(null)}
        />
      ) : (
        <Dialog open={!!quickPreviewDoc} onOpenChange={(open) => !open && setQuickPreviewDoc(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 truncate">
                <Eye className="w-5 h-5 shrink-0" />
                <span className="truncate">{quickPreviewDoc?.title}</span>
              </DialogTitle>
            </DialogHeader>
            {quickPreviewDoc && (
              <div className="flex-1 min-h-0 overflow-auto">
                <QuickPreviewContent doc={quickPreviewDoc} />
              </div>
            )}
            <DialogFooter className="flex-row gap-2 sm:justify-between">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="select-preview-doc"
                  checked={quickPreviewDoc ? selectedDocs.has(quickPreviewDoc.id) : false}
                  onCheckedChange={() => quickPreviewDoc && toggleDocSelection(quickPreviewDoc.id)}
                />
                <label htmlFor="select-preview-doc" className="text-sm cursor-pointer">
                  Sélectionner pour suppression
                </label>
              </div>
              <Button variant="outline" onClick={() => setQuickPreviewDoc(null)}>
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog with Gallery Mode */}
      <Dialog open={deleteConfirmOpen} onOpenChange={(open) => {
        setDeleteConfirmOpen(open);
        if (!open) {
          setGalleryMode(false);
          setGalleryIndex(0);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="w-5 h-5" />
                Confirmer la suppression
              </DialogTitle>
              <div className="flex items-center gap-1">
                <Button
                  variant={galleryMode ? "ghost" : "secondary"}
                  size="sm"
                  onClick={() => setGalleryMode(false)}
                  className="h-8 px-2"
                >
                  <Grid className="w-4 h-4" />
                </Button>
                <Button
                  variant={galleryMode ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => { setGalleryMode(true); setGalleryIndex(0); }}
                  className="h-8 px-2"
                >
                  <Eye className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <DialogDescription>
              {galleryMode 
                ? `Document ${galleryIndex + 1} sur ${selectedDocs.size} - Utilisez ← → pour naviguer`
                : `Vous êtes sur le point de supprimer ${selectedDocs.size} document(s). Cette action est irréversible.`
              }
            </DialogDescription>
          </DialogHeader>
          
          {galleryMode ? (
            // Gallery/Carousel View
            <DeleteGalleryView 
              documents={documents.filter(doc => selectedDocs.has(doc.id))}
              currentIndex={galleryIndex}
              onIndexChange={setGalleryIndex}
              onDeselect={(docId) => {
                const newSelected = new Set(selectedDocs);
                newSelected.delete(docId);
                setSelectedDocs(newSelected);
                // Adjust index if needed
                if (galleryIndex >= newSelected.size) {
                  setGalleryIndex(Math.max(0, newSelected.size - 1));
                }
              }}
              onBack={() => setGalleryMode(false)}
              formatDate={formatDate}
            />
          ) : (
            // Grid View (original)
            <div className="flex-1 min-h-0 overflow-auto my-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {documents.filter(doc => selectedDocs.has(doc.id)).map((doc, idx) => {
                  const typeConfig = getTypeConfig(doc.doc_type);
                  return (
                    <div
                      key={doc.id}
                      className="border border-destructive/20 rounded-lg p-3 bg-destructive/5 relative group"
                    >
                      {/* Deselect button */}
                      <button
                        className="absolute top-1 right-1 z-10 p-1 rounded-full bg-background/80 hover:bg-background border shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newSelected = new Set(selectedDocs);
                          newSelected.delete(doc.id);
                          setSelectedDocs(newSelected);
                        }}
                        title="Désélectionner"
                      >
                        <Square className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                      
                      {/* Clickable area for preview */}
                      <div 
                        className="cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => { setGalleryMode(true); setGalleryIndex(idx); }}
                      >
                        <DocumentThumbnail doc={doc} className="w-full h-24 mb-2 rounded" />
                        <p className="text-xs font-medium line-clamp-2 leading-tight">{doc.title}</p>
                        <div className="flex items-center gap-1 mt-2 flex-wrap">
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground whitespace-nowrap">
                            {typeConfig.label}
                          </span>
                          {doc.mime_type && (
                            <span className="text-[8px] px-1 py-0.5 rounded bg-muted/50 text-muted-foreground/70 uppercase font-mono">
                              {doc.mime_type.includes('pdf') ? 'PDF' : 
                               doc.mime_type.includes('image') ? 'IMG' : '?'}
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {formatDate(doc.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={deleting}
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Supprimer {selectedDocs.size} document(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Collector */}
      <DocumentCollector open={collectorOpen} onOpenChange={setCollectorOpen} />

      {/* Duplicate Preview Sheet */}
      <DocumentPreviewSheet
        document={duplicatePreviewDoc}
        open={!!duplicatePreviewDoc}
        onOpenChange={(open) => !open && setDuplicatePreviewDoc(null)}
      />

      {/* Move to Folder Dialog */}
      <MoveToFolderDialog
        open={!!moveDoc}
        onOpenChange={(open) => !open && setMoveDoc(null)}
        onMove={handleMoveToFolder}
        currentFolderId={moveDoc?.folder_id}
        documentTitle={moveDoc?.title}
      />
    </div>
  );
};

export default Documents;
